import { post, get } from '@/util/request.js';
import { getToken } from '@/util/jwt-cache.js';
import {
	type UserTypes,
	type BaseAccount,
	type CountryUtil,
	type UserCertification,
	SupAccount,
} from '@fibl/oxs-common';
import { Auth, EAV, SearchRepo } from '@fibl/zdb-common';

export async function listAccountsForUser(ssoUserId: string): Promise<EAV.Entity<UserTypes.UnknownAcc, UserTypes.AccountCommonIndexFields>[]> {
	// const auth = getToken();
	if (!ssoUserId) return [];
	// return post('auth/domain/listaccounts', { includeData: true }, auth);
	return SearchRepo.findAll('account', { field: '/data/linkedUsers', comparison: 'eq', value: ssoUserId }) as Promise<any[]>
}

export async function selfRegisterSupAccount(
	token: string,
	userData: UserTypes.UserDataSup,
): Promise<string> {
	const payload = {
		registrationName: 'sup',
		userData: userData,
		token: token,
	};
	const { account } = await post('auth/domain/register', payload);
	return account;
}

export async function registerWithDomainByInvite(
	inviteId: string,
	registrationName: string,
	token: string,
): Promise<string> {
	const payload = {
		inviteId,
		registrationName,
		userData: {},
		token,
	};
	const { account } = await post('auth/domain/register', payload);
	return account;
}

export async function checkInviteStatus(
	inviteId: string,
): Promise<{ status: 'open' | 'closed' | 'gone' | 'expired' }> {
	const res = await post('auth/domain/invitestatus', { inviteId });
	return res;
}

export async function getAccountDetails(): Promise<UserTypes.UnknownAcc> {
	return get('auth/account/getaccount', null, await getToken());
}

export async function unregisterAccount(account: string) {
	return post('auth/account/unregisterwithaccount', { account }, await getToken());
}

export async function updateAccount(data: Record<string,any>, surpressError?: boolean) {
	return post('auth/account/updateaccount', data, await getToken(), surpressError);
}

type LoginResponse = {
	jwt: string;
	domain: string;
	expires: number;
};

export async function loginToDomain(token: string, useAccount?: string): Promise<LoginResponse> {
	const p: any = { token };
	if (useAccount) p.useAccount = useAccount;
	try {
		return await post('auth/domain/login', p, false);
	} catch (e: any) {
		if (e.message.includes('unable to choose account') && useAccount) {
			return loginToDomain(token);
		} else {
			throw e;
		}
	}
}

export async function managementCreateAccount(
	regName: string,
	reqConfirm: boolean,
	userData: Record<string, any>,
	staticData: Record<string, any>,
) {
	let res = await post('auth/accountmanage/createaccount', {
		registrationName: regName,
		requiresConfirmation: reqConfirm,
		userData,
		staticData,
	});
	return res;
}

export async function managementUpdateAccount(account: BaseAccount) {
	let res = await post('auth/accountmanage/updateaccount', {
		account: account.getId(),
		userData: account.getUserData(),
		staticData: account.getStaticData(),
		maxUsers: account.getMaxUsers(),
		active: account.getActive(),
	});
	return res;
}

export async function managementCreateInvite(account: string): Promise<string> {
	let res = await post('auth/accountmanage/createaccountinvite', { account });
	return res.inviteId;
}

export async function managementUnlinkAccount(account: string, ssoUser: string): Promise<string> {
	let res = await post('auth/accountmanage/unlinkaccount', { account, ssoUser });
	return res;
}

export function getUserInfos(ids: string[]): Promise<Auth.GetUserInfosForIdsResponse> {
	const req: Auth.GetUserInfosForIdsRequest = { userIds: ids };
	return post('auth/keycloak/getuserinfos', req);
}

export function unlinkUser(id: string) {
	const config: UserTypes.UnlinkAgrForCon = {
		agrId: id,
	};
	return post('oxs/user/unlinkuserforconsultant', config);
}

export function registerAgr(data: {
	controlNumber: string;
	postalcode: string;
	cc: CountryUtil.CountryCode;
	ins: string;
	ssoToken: string;
}): Promise<string> {
	return post('oxs/user/registeragr', data);
}

export function updateAutApprovalStatusForSup(data: {
	supId: string;
	countryCode: CountryUtil.CountryCode;
	status: UserTypes.AutApprovalStatusLabels;
}) {
	return post('oxs/user/updateautapprovalstatusforsup', data);
}

export function updateAutAcceptOffersForSup(data: {
	supId: string;
	countryCode: CountryUtil.CountryCode;
	autoAcceptConfig: UserTypes.AutoAcceptConfig;
}) {
	return post('oxs/user/updateautacceptoffersforsup', data);
}

